<script lang="ts">
    
    import { createEventDispatcher } from "svelte";
    import { Alert, Button, FormCheck, FormGroup, Icon } from "sveltestrap";
    import type { User } from "../../Model/User";
    import { usersService } from "../../Services";
    import { showNotification } from "../../Services/ui";
    import ControlFeedback from "../ControlFeedback.svelte";
    import ControlLabel from "../ControlLabel.svelte";
    import FormFeedback from "../FormFeedback.svelte";
    import InvalidFeedback from "../InvalidFeedback.svelte";
    import UserTypeSelect from "./UserTypeSelect.svelte";

    export let user: User;

    const dispatch = createEventDispatcher();

    let name: string;
    let email: string;
    let typeId: number;
    let enabled: boolean;
    let password: string;
    let rePassword: string;
    let isSaving = false;
    let formFeedback: FormFeedback;

    $: setupUser(user);

    function setupUser(user: User): void {
        name = user.name.value;
        email = user.email.value;
        typeId = user.typeId.value;
        enabled = user.enabled.value;
        password = '';
        rePassword = '';
    }

    async function onSubmit(): Promise<void> {
        if (isSaving || !formFeedback.isValid()) {
            return;
        }
        try {
            isSaving = true;

            //Creamos un clon del usuario con los nuevos datos
            //del formulario para el guardado
            const clon = user.clone();
            clon.name.set(name);
            clon.email.set(email);
            clon.typeId.set(typeId);
            clon.enabled.set(enabled);
            
            await usersService.saveUser(clon, password || undefined);

            //El usuario ha sido guardado
            //Seteamos las propiedades del usuario
            //con los valores del formulario
            user.refresh(clon);

            password = '';
            rePassword = '';

            showNotification({
                type: 'success',
                content: 'El usuario ha sido guardado'
            });

            dispatch('saved');

        } catch (error) {
            console.error(error);

            showNotification({
                type: 'success',
                content: 'Ha ocurrido un error al guardar el usuario    '
            });

        } finally {
            isSaving = false;
        }
    }

</script>

<FormFeedback bind:this={formFeedback} let:isInvalid>
        
    <FormGroup>
        <ControlFeedback let:valueMissing let:input let:invalid>
  
            <ControlLabel required invalid={input && invalid}>Nombre</ControlLabel>

            <input type="text" required class="form-control" bind:value={name}>

            <InvalidFeedback show={input && valueMissing}>
                <Icon name="exclamation-triangle" /> Debe indicar el nombre del usuario.
            </InvalidFeedback>

        </ControlFeedback>
    </FormGroup>

    <FormGroup>
        <ControlFeedback let:valueMissing let:typeMismatch let:input let:invalid>

            <ControlLabel required invalid={input && invalid}>Email</ControlLabel>

            <input type="email" required class="form-control" bind:value={email}>

            <InvalidFeedback show={input && valueMissing}>
                <Icon name="exclamation-triangle" /> Debe indicar una dirección de correo.
            </InvalidFeedback>

            <InvalidFeedback show={typeMismatch}>
                <Icon name="exclamation-triangle" /> Revise el formato de la dirección de correo.
            </InvalidFeedback>

        </ControlFeedback>
    </FormGroup>

    <FormGroup>
        <ControlLabel>Tipo de usuario</ControlLabel>
        <UserTypeSelect bind:value={typeId}></UserTypeSelect>
    </FormGroup>

    <FormGroup>
        <ControlFeedback let:tooShort let:control let:invalid>

            <ControlLabel {invalid}>Contraseña</ControlLabel>

            <input type="password" minlength="5" class="form-control" bind:value={password}>

            <InvalidFeedback show={password && tooShort}>
                <Icon name="exclamation-triangle" /> La longitud mínima de la contraseña es de {control.minLength} caracteres
            </InvalidFeedback>

        </ControlFeedback>
    </FormGroup>

    <FormGroup>
        <ControlFeedback override={password ? password === rePassword : undefined} let:invalid>
            <ControlLabel {invalid}>Repetir contraseña</ControlLabel>
            <input type="password" class="form-control" bind:value={rePassword}>
            <InvalidFeedback show={invalid}>
                <Icon name="exclamation-triangle" /> Las contraseñas no coinciden
            </InvalidFeedback>
        </ControlFeedback>
    </FormGroup>

    {#if isInvalid}
        <Alert color="warning">
            Rellene correctamente todos los campos requeridos del formulario.
        </Alert>
    {/if}

    <div class="f-between f-x-center">
        <FormCheck label="Activado" bind:checked={enabled}></FormCheck>
        <Button type="button" color="primary" on:click={onSubmit} disabled={isInvalid || isSaving}>
            {#if isSaving}
                <Icon name="cloud-upload" /> Guardando...
            {:else}
                <Icon name="cloud-upload" /> Guardar
            {/if}
        </Button>
    </div>

</FormFeedback>